import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { CheckCircle2, Circle, Clock } from "lucide-react";
import { apiRequest } from "../../Api/Api";
import { ENDPOINTS } from "../../Api/Endpoints";
import { showAlert } from "../../feedback/feedbackApi";
import { normalizePatientAppointments } from "../../utils/patientAppointmentsNormalize";
import {
  PATIENT_JOURNEY_STEPS,
  getPatientJourneyStepClasses,
  journeyPillText,
  journeyBannerPill,
} from "../../utils/patientJourney";
import "./RequestJourneyPanel.scss";

export { journeyPillText, journeyBannerPill };

const STEPS = PATIENT_JOURNEY_STEPS;

/**
 * Appointment step visual state from real /app/appointments rows (not request workflow alone).
 * @returns {'done' | 'current' | 'todo'}
 */
export function getAppointmentJourneyStepClass(appointments) {
  const rows = Array.isArray(appointments) ? appointments : [];
  const st = (s) => String(s || "").toLowerCase();
  const hasConfirmed = rows.some((a) => {
    const s = st(a.status);
    return s === "scheduled" || s === "completed";
  });
  const hasPending = rows.some((a) => st(a.status) === "pending_approval");
  if (hasConfirmed) return "done";
  if (hasPending) return "current";
  return "todo";
}

function pickPrimaryAppointmentRow(rows) {
  const list = Array.isArray(rows) ? rows : [];
  const st = (s) => String(s || "").toLowerCase();
  const scheduled = list.filter((a) => st(a.status) === "scheduled");
  if (scheduled.length) return scheduled[0];
  const completed = list.filter((a) => st(a.status) === "completed");
  if (completed.length) return completed[0];
  return list[0] || null;
}

/** Doctor / channel count the patient's bid was shared with (from list or detail row). */
function doctorShareCountFromRequest(request, matchRequestId) {
  if (!request || matchRequestId == null || matchRequestId === "") return null;
  if (String(request.matchRequestId || "") !== String(matchRequestId)) return null;
  const raw = request?.providerShare?.totalChannels ?? request?.providerResponseCount;
  const n = Number(raw);
  if (!Number.isFinite(n) || n < 0) return null;
  return Math.floor(n);
}

/** @deprecated Prefer getPatientJourneyStepClasses — kept for older imports. */
export function getJourneyStepClasses(workflow) {
  const c = getPatientJourneyStepClasses(workflow);
  return {
    upload: c.documents,
    admin: c.admin,
    bidding: c.replies,
    appointment: c.book,
    documents: c.documents,
    budget: c.budget,
    replies: c.replies,
    book: c.book,
  };
}

/**
 * @param {object} props
 * @param {string} props.matchRequestId
 * @param {string} [props.workflow]
 * @param {string} [props.pillTextOverride] — optional; defaults to journey banner ("In progress", etc.)
 * @param {'sidebar'|'embedded'} [props.layout]
 * @param {object} [props.requestRow] — pass-through for navigation state when opening request details
 */
export default function RequestJourneyPanel({
  matchRequestId,
  workflow,
  pillTextOverride,
  layout = "sidebar",
  requestRow = null,
}) {
  const navigate = useNavigate();
  const id = matchRequestId != null && matchRequestId !== "" ? String(matchRequestId) : "";
  const pill = pillTextOverride ?? journeyBannerPill(workflow);
  const [appointmentRows, setAppointmentRows] = useState([]);

  useEffect(() => {
    let cancelled = false;
    if (!id) {
      setAppointmentRows([]);
      return () => {
        cancelled = true;
      };
    }
    (async () => {
      try {
        const res = await apiRequest({
          method: "GET",
          url: ENDPOINTS.GET_MY_APPOINTMENTS,
          params: { matchRequestId: id },
        });
        const rows = normalizePatientAppointments(res ?? {});
        if (!cancelled) setAppointmentRows(rows);
      } catch {
        if (!cancelled) setAppointmentRows([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [id]);

  const stepMap = useMemo(() => {
    const base = getPatientJourneyStepClasses(workflow);
    return {
      ...base,
      book: getAppointmentJourneyStepClass(appointmentRows),
    };
  }, [workflow, appointmentRows]);

  const persistCarePriceContext = useCallback(() => {
    if (!id) return;
    try {
      window.localStorage.setItem("healbid:careprice:lastMatchRequestId", id);
    } catch (_) {}
  }, [id]);

  const onUpload = () => {
    if (!id) return;
    const row =
      requestRow && String(requestRow.matchRequestId) === id
        ? requestRow
        : { matchRequestId: id, requestWorkflowStatus: workflow };
    navigate(`/patient-match-request/${encodeURIComponent(id)}`, { state: { requestRow: row } });
  };

  const onAdminReview = () => {
    if (!id) return;
    navigate(`/request/${id}/review`);
  };

  const onBudget = useCallback(() => {
    const u = String(workflow || "").toUpperCase();
    if (u === "PENDING_ADMIN_REVIEW" || u === "REJECTED") {
      navigate(`/request/${id}/review`);
      return;
    }
    persistCarePriceContext();
    navigate("/care-price", { state: { matchRequestId: id } });
  }, [workflow, id, navigate, persistCarePriceContext]);

  const onReplies = useCallback(async () => {
    const u = String(workflow || "").toUpperCase();
    if (u === "PENDING_ADMIN_REVIEW" || u === "REJECTED") {
      const row =
        requestRow && String(requestRow.matchRequestId) === id
          ? requestRow
          : { matchRequestId: id, requestWorkflowStatus: workflow };
      navigate(`/patient-match-request/${encodeURIComponent(id)}`, { state: { requestRow: row } });
      return;
    }
    if (u === "VERIFIED") {
      persistCarePriceContext();
      navigate("/care-price", { state: { matchRequestId: id } });
      return;
    }
    if (!["BIDDING", "BID_SELECTED", "BOOKED", "VISIT_COMPLETED"].includes(u)) {
      navigate(`/patient-match-request/${encodeURIComponent(id)}`, {
        state: {
          requestRow:
            requestRow && String(requestRow.matchRequestId) === id
              ? requestRow
              : { matchRequestId: id, requestWorkflowStatus: workflow },
        },
      });
      return;
    }

    let doctorCount = doctorShareCountFromRequest(requestRow, id);
    if (doctorCount == null) {
      try {
        const res = await apiRequest({ method: "GET", url: ENDPOINTS.GET_MATCH_REQUEST_DETAIL(id) });
        doctorCount = doctorShareCountFromRequest(res?.request, id);
      } catch {
        doctorCount = null;
      }
    }

    if (doctorCount != null && doctorCount > 0) {
      showAlert(
        `Your budget is shared with ${doctorCount} doctor${doctorCount === 1 ? "" : "s"}. Replies appear in Messages.`,
        { title: "Budget shared", variant: "info" },
      );
      navigate("/patient-chats");
      return;
    }
    if (doctorCount === 0) {
      showAlert(
        "Your budget is shared. Matching doctors and hospitals will reply in Messages.",
        { title: "Budget shared", variant: "info" },
      );
      navigate("/patient-chats");
      return;
    }
    showAlert(
      "Your budget is shared with matching doctors. Watch Messages for replies.",
      { title: "Budget shared", variant: "info" },
    );
    navigate("/patient-chats");
  }, [workflow, requestRow, id, navigate, persistCarePriceContext]);

  const onAppointment = useCallback(() => {
    const apptStep = getAppointmentJourneyStepClass(appointmentRows);
    if (apptStep === "done") {
      const row = pickPrimaryAppointmentRow(appointmentRows);
      if (row?.id) {
        navigate("/appointment-confirmed", { state: { appointment: row } });
        return;
      }
      navigate("/appointment");
      return;
    }
    if (apptStep === "current") {
      showAlert(
        "Your visit request is waiting for the doctor/hospital and admin. You will get a notification when the appointment is confirmed.",
        { title: "Appointment pending", variant: "info" },
      );
      return;
    }
    showAlert(
      "Choose a doctor reply and book your visit. After you send a booking request, this step will update.",
      {
        title: "Visit not booked yet",
        variant: "info",
        actions: [
          {
            label: "Open messages",
            onClick: () => {
              navigate("/patient-chats");
            },
          },
        ],
      },
    );
  }, [appointmentRows, navigate, persistCarePriceContext, id]);

  const handlers = [onUpload, onAdminReview, onBudget, onReplies, onAppointment];

  if (!id) return null;

  const cardClass = `jtCard${layout === "embedded" ? " jtCard--embedded" : ""}`;

  return (
    <div className={cardClass}>
      <div className="jtHead">
        <h3 className="jtKicker">Journey</h3>
        <span className="jtChip">{pill}</span>
      </div>
      <ol className="jtOl" aria-label="Journey steps">
        {STEPS.map((s, i) => {
          const stepClass = stepMap[s.key];
          const lineDone = stepClass === "done";
          const titleActive = stepClass === "done" || stepClass === "current";
          const iconSz = layout === "embedded" ? 14 : 16;
          const clockSz = layout === "embedded" ? 12 : 14;
          const circleSz = layout === "embedded" ? 10 : 12;

          return (
            <li key={s.id} className="jtStep">
              {i < STEPS.length - 1 ? (
                <span
                  className={`jtConnector${lineDone ? " jtConnector--primary" : ""}`}
                  aria-hidden
                />
              ) : null}
              <button
                type="button"
                className="jtStepBtn"
                onClick={() => {
                  const fn = handlers[i];
                  if (typeof fn === "function") void fn();
                }}
              >
                <div className="jtIconSlot">
                  {stepClass === "done" ? (
                    <div className="jtIconDone">
                      <CheckCircle2 width={iconSz} height={iconSz} strokeWidth={2} aria-hidden />
                    </div>
                  ) : stepClass === "current" ? (
                    <div className="jtIconCurrent">
                      <Clock width={clockSz} height={clockSz} strokeWidth={2} aria-hidden />
                    </div>
                  ) : (
                    <div className="jtIconTodo">
                      <Circle width={circleSz} height={circleSz} strokeWidth={2} aria-hidden />
                    </div>
                  )}
                </div>
                <div className="jtBody">
                  <div className={`jtTitle ${titleActive ? "jtTitle--active" : "jtTitle--muted"}`}>
                    {i + 1}. {s.label}
                  </div>
                  <div className="jtDesc">{s.desc}</div>
                </div>
              </button>
            </li>
          );
        })}
      </ol>
    </div>
  );
}
